import React, { useState } from "react";
import { STATUS_STYLE, ui, type DocStatus, type Lang, type TechPackEntry } from "./shared.js";
import { getFreshToken } from "./auth.js";
import { notifyDenied } from "./access_denial_toast.js";

export interface FolderEntry { id: string; name: string; is_shared?: boolean; is_owner?: boolean; }
// A roster entry is either an accepted member (user_id + avatar_color set) or a
// still-pending invite (status 'pending', invite_id set, display_name = email).
interface MemberRow { user_id: string | null; display_name: string; avatar_color: string | null; role: string; status?: string; invite_id?: string | null; }

interface PackFolderListProps {
  lang: Lang;
  folders: FolderEntry[];                  // already filtered to the active tab's area
  packs: TechPackEntry[];                  // full pack list (membership read via packFolders)
  rootPacks: TechPackEntry[];              // parent-computed unfiled packs to show at root ([] in shared tab)
  packFolders: Record<string, string>;    // packId -> folderId
  expanded: Record<string, boolean>;       // folderId -> expanded (UI only)
  editingFolderId: string | null;
  editingFolderName: string;
  folderEditRef: React.RefObject<HTMLInputElement>;
  onToggleExpand: (folderId: string) => void;
  onSelect: (pack: TechPackEntry) => void;
  onDeletePack: (id: string) => void;
  onMove: (packId: string, folderId: string | null) => void;
  onStartRename: (id: string, name: string) => void;
  onRenameChange: (name: string) => void;
  onCommitRename: () => void;
  onCancelRename: () => void;
  onRemoveFolder: (id: string) => void;
  onToggleShared: (folderId: string, next: boolean) => void;
  activeFolderId?: string | null;                 // Surface Activity scope (highlight)
  onActiveFolderChange?: (folderId: string) => void;
}

const MONO = "'Space Mono', monospace";

export function PackFolderList(props: PackFolderListProps) {
  const {
    lang, folders, packs, rootPacks, packFolders, expanded,
    editingFolderId, editingFolderName, folderEditRef,
    onToggleExpand, onSelect, onDeletePack, onMove,
    onStartRename, onRenameChange, onCommitRename, onCancelRename, onRemoveFolder, onToggleShared,
    activeFolderId, onActiveFolderChange,
  } = props;

  const [hoveredPackId,   setHovered]       = useState<string | null>(null);
  const [moveMenuId,      setMoveMenuId]    = useState<string | null>(null);
  const [inviteFolderId,  setInviteFId]     = useState<string | null>(null);
  const [inviteEmail,     setInviteEmail]   = useState("");
  const [inviteStatus,    setInviteStatus]  = useState<"idle"|"generating"|"ready"|"error"|"full"|"invalid"|"already">("idle");
  const [inviteLink,      setInviteLink]    = useState("");
  const [copied,          setCopied]        = useState(false);
  // SHARE-TOGGLE-1 P0: owner-only collaborator roster (per folder) + in-flight kick.
  const [members,         setMembers]       = useState<Record<string, MemberRow[]>>({});
  const [kicking,         setKicking]       = useState<string | null>(null);

  // Load the collaborator roster for a folder (owner-only; server returns [] to
  // non-owners). Drives the kick (×) buttons in the invite panel.
  async function loadMembers(folderId: string) {
    const token = await getFreshToken();
    if (!token) return;
    try {
      const res = await fetch(`/api/studio/folders/${folderId}/members`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const rows = await res.json() as MemberRow[]; setMembers(prev => ({ ...prev, [folderId]: rows })); }
    } catch { /* keep prior roster */ }
  }

  // Owner removes a single collaborator (§5.4b). Severs their access + releases
  // their seat server-side; here we just refresh the roster.
  async function kickMember(folderId: string, userId: string) {
    setKicking(userId);
    const token = await getFreshToken();
    if (!token) { setKicking(null); return; }
    try {
      const res = await fetch(`/api/studio/folders/${folderId}/members/${userId}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (res.ok || res.status === 404) await loadMembers(folderId);
    } catch { /* leave roster as-is; owner can retry */ }
    finally { setKicking(null); }
  }

  // Owner cancels a still-pending invite (the invitee never accepted). Frees the
  // seat + invalidates the link server-side; here we just refresh the roster.
  async function cancelInvite(folderId: string, inviteId: string) {
    setKicking(inviteId);
    const token = await getFreshToken();
    if (!token) { setKicking(null); return; }
    try {
      const res = await fetch(`/api/studio/folders/${folderId}/invites/${inviteId}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (res.ok || res.status === 404) await loadMembers(folderId);
    } catch { /* leave roster as-is; owner can retry */ }
    finally { setKicking(null); }
  }

  // Generates an invite link only — does NOT send email/WeChat (delivery is a
  // pre-public-beta task, logged as debt D-送达). The owner copies the link.
  async function generateLink(folderId: string) {
    if (!inviteEmail.trim()) return;
    setInviteStatus("generating"); setInviteLink(""); setCopied(false);
    const token = await getFreshToken();
    if (!token) { setInviteStatus("error"); return; }
    try {
      const res = await fetch("/api/studio/invites", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ folder_id: folderId, invitee_email: inviteEmail.trim() }),
      });
      const data = await res.json() as { invite_link?: string; error?: string };
      if (res.ok) { setInviteLink(data.invite_link ?? ""); setInviteStatus("ready"); }
      else if (res.status === 409 && data.error === "seat_limit_reached") setInviteStatus("full");
      else if (res.status === 409 && data.error === "invite_already_pending") setInviteStatus("already");
      else if (res.status === 400 && data.error === "invalid_email") setInviteStatus("invalid");
      else setInviteStatus("error");
    } catch { setInviteStatus("error"); }
  }

  function copyInviteLink() {
    const full = `${window.location.origin}${inviteLink}`;
    void navigator.clipboard?.writeText(full)
      .then(() => { setCopied(true); window.setTimeout(() => setCopied(false), 1500); })
      .catch(() => { /* clipboard blocked — link is still visible to select */ });
  }

  function openInvite(folderId: string) {
    const opening = inviteFolderId !== folderId;
    setInviteFId(prev => prev === folderId ? null : folderId);
    setInviteEmail(""); setInviteStatus("idle"); setInviteLink(""); setCopied(false);
    onActiveFolderChange?.(folderId);  // focus this folder's Surface Activity
    if (opening) void loadMembers(folderId);  // owner roster for the kick UI
  }

  function membersLabel(): string {
    return lang === "zh" ? "协作者" : lang === "fr" ? "Collaborateurs" : lang === "it" ? "Collaboratori" : "Collaborators";
  }
  function removeTitle(): string {
    return lang === "zh" ? "移除协作者（立即撤销访问）" : "Remove collaborator (revoke access immediately)";
  }
  function pendingTag(): string {
    return lang === "zh" ? "待接受" : "PENDING";
  }
  function pendingTitle(): string {
    return lang === "zh" ? "尚未接受邀请 — 占用一个席位。点击 × 撤销邀请并释放席位。"
      : "Hasn't accepted yet — holds a seat. Click × to cancel the invite and free the seat.";
  }

  const folderExists = (fid: string | undefined) => !!fid && folders.some(f => f.id === fid);

  function moveLabel(): string {
    return lang === "zh" ? "移动到…" : lang === "fr" ? "Déplacer…" : lang === "it" ? "Sposta…" : "Move to…";
  }
  function rootLabel(): string {
    return lang === "zh" ? "▲ 根目录（移出文件夹）" : lang === "fr" ? "▲ Racine" : lang === "it" ? "▲ Radice" : "▲ Root (no folder)";
  }
  function shareTitle(isShared: boolean): string {
    if (isShared) return lang === "zh" ? "移回私有" : lang === "fr" ? "Rendre privé" : lang === "it" ? "Rendi privato" : "Make private";
    return lang === "zh" ? "移到共享区" : lang === "fr" ? "Partager" : lang === "it" ? "Condividi" : "Move to shared";
  }

  // ejectOnly = the caller is the FOLDER owner acting on a pack a member
  // contributed (Q2). They may ONLY eject it (→ the creator's private area); they
  // cannot relocate it into their own folders or delete it. The menu collapses to
  // a single "remove from shared folder" action.
  function renderMoveMenu(pack: TechPackEntry, ejectOnly: boolean) {
    const currentFolder = packFolders[pack.id];
    if (ejectOnly) {
      return (
        <div
          onClick={e => e.stopPropagation()}
          style={{ position:"absolute", right:8, top:"100%", zIndex:1000, background:"#1a1a1a", border:"1px solid #3a3a3a", boxShadow:"0 4px 16px rgba(0,0,0,0.6)", minWidth:200 }}
        >
          <button onClick={() => { onMove(pack.id, null); setMoveMenuId(null); }} style={{ display:"block", width:"100%", textAlign:"left", padding:"9px 14px", background:"transparent", border:"none", color:"#e0a055", fontFamily:MONO, fontSize:11, cursor:"pointer" }}>{ui("pack_eject_from_folder", lang)}</button>
        </div>
      );
    }
    return (
      <div
        onClick={e => e.stopPropagation()}
        style={{ position:"absolute", right:8, top:"100%", zIndex:1000, background:"#1a1a1a", border:"1px solid #3a3a3a", boxShadow:"0 4px 16px rgba(0,0,0,0.6)", minWidth:180, maxHeight:240, overflowY:"auto" }}
      >
        <div style={{ padding:"6px 14px", fontFamily:"'Rama Gothic E', system-ui", fontSize:"0.5rem", letterSpacing:"0.1em", color:"#666", borderBottom:"1px solid #2a2a2a" }}>{moveLabel()}</div>
        {folderExists(currentFolder) && (
          <button onClick={() => { onMove(pack.id, null); setMoveMenuId(null); }} style={{ display:"block", width:"100%", textAlign:"left", padding:"7px 14px", background:"transparent", border:"none", color:"#e0a055", fontFamily:MONO, fontSize:11, cursor:"pointer", borderBottom:"1px solid #2a2a2a" }}>{rootLabel()}</button>
        )}
        {folders.filter(f => f.id !== currentFolder).map(f => (
          <button key={f.id} onClick={() => { onMove(pack.id, f.id); setMoveMenuId(null); }} style={{ display:"block", width:"100%", textAlign:"left", padding:"7px 14px", background:"transparent", border:"none", color:"var(--text-muted)", fontFamily:MONO, fontSize:11, cursor:"pointer" }}>📁 {f.name}</button>
        ))}
        {folders.filter(f => f.id !== currentFolder).length === 0 && !folderExists(currentFolder) && (
          <div style={{ padding:"8px 14px", fontFamily:MONO, fontSize:11, color:"#444" }}>{lang === "zh" ? "暂无文件夹" : "No folders"}</div>
        )}
      </div>
    );
  }

  function renderPackRow(pack: TechPackEntry, indented: boolean) {
    const sc = STATUS_STYLE[pack.status?.toLowerCase() as DocStatus] ?? STATUS_STYLE['draft'];
    return (
      <div
        key={pack.id}
        onClick={() => onSelect(pack)}
        onMouseEnter={() => setHovered(pack.id)}
        onMouseLeave={() => setHovered(null)}
        style={{ position:"relative", display:"flex", alignItems:"center", padding:"12px 20px", paddingLeft: indented ? 44 : 20, background: hoveredPackId===pack.id ? "rgba(255,255,255,0.03)" : "transparent", borderBottom:"1px solid #111", cursor:"pointer" }}
      >
        <div style={{ flex:1, minWidth:0 }}>
          {/* IP-FALLBACK-1: a just-created pack has no style_number until its first
              autosave persists — show a neutral localized placeholder, NEVER the
              internal UUID (UUID = machine key; style_number is the human-facing id). */}
          {/* One line = style_number only (founder, test 10): the style-number
              encoding will carry category+season itself, and edit history belongs
              in the left Activity log (P2a-3) — nothing renders under the name. */}
          <div style={{ fontFamily:MONO, fontSize:12, fontWeight:700, letterSpacing:"0.05em", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", color:"var(--text)" }}>{pack.styleNumber || (lang==="zh" ? "（未命名）" : "(unnamed)")}</div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:10, flexShrink:0, marginLeft:12 }}>
          <span style={{ fontFamily:"'Rama Gothic E', system-ui", fontSize:"0.55rem", letterSpacing:"0.1em", color:sc.color, border:`1px solid ${sc.color}`, padding:"2px 6px", textTransform:"uppercase" }}>{pack.status}</span>
          <span style={{ fontFamily:MONO, fontSize:10, color:"#666" }}>{new Date(pack.updatedAt).toLocaleDateString()}</span>
          {/* Diagram authority model (founder, 2026-06-13). `hasAuthority` =
              folder owner (or creator of an unfiled pack). A plain collaborator
              (isOwner === false) keeps open+edit+save but NO controls. The folder
              owner has FULL control — including the × delete/remove — on EVERY
              pack in the folder:
                • own pack  → ⇄ move (own folders / root) + × destroy
                • a member's contribution → × only, which REMOVES it from the
                  shared folder (server ejects it back to the creator's private;
                  A & others lose access, the creator keeps ownership). No ⇄
                  (A cannot seize/relocate another member's asset). */}
          {(() => {
            const hasAuthority = pack.isOwner === true;
            if (!hasAuthority) return null;
            const isOwnPack = pack.isPackCreator === true; // A created it (vs a member's contribution)
            return (
              <>
                {isOwnPack && (
                  <button
                    onClick={e => { e.stopPropagation(); setMoveMenuId(moveMenuId === pack.id ? null : pack.id); }}
                    title={moveLabel()}
                    style={{ background:"transparent", border:"none", color: moveMenuId===pack.id ? "var(--text)" : "#666", cursor:"pointer", fontSize:15, lineHeight:1, padding:"2px 4px" }}
                  >⇄</button>
                )}
                {/* Remove (×) is ALWAYS visible for the folder owner (not hover-
                    gated) so the option is discoverable on every pack; dimmed until
                    hovered. A destroys OWN packs / ejects a member's (per §2). */}
                <button
                  onClick={e => {
                    e.stopPropagation();
                    // AUTHORIZATION_CONTRACT §2: A destroys OWN packs, but
                    // "deletes" a member's contribution by EJECTING it (→ folder
                    // null), not hard-deleting (the DELETE endpoint is creator-
                    // only and would 403). Routing × → eject lets A remove B/C's
                    // shared packs from the folder per the contract.
                    if (isOwnPack) onDeletePack(pack.id);
                    else onMove(pack.id, null);
                  }}
                  title={isOwnPack ? undefined : ui("pack_eject_from_folder", lang)}
                  style={{ background:"transparent", border:"none", color:"#e05555", cursor:"pointer", fontSize:14, padding:"4px 6px", opacity: hoveredPackId===pack.id ? 1 : 0.55 }}
                >×</button>
                {isOwnPack && moveMenuId===pack.id && renderMoveMenu(pack, false)}
              </>
            );
          })()}
        </div>
      </div>
    );
  }

  return (
    <>
      {folders.map(folder => {
        const inFolder = packs.filter(p => packFolders[p.id] === folder.id);
        const isExp = !!expanded[folder.id];
        // P1a: owner-only folder admin controls (rename / share / INVITE / delete /
        // kick-roster). Hidden for a collaborator (folder.is_owner === false);
        // server enforces owner-only regardless. FAIL CLOSED: a missing/undefined
        // flag hides the owner controls (explicit-true check).
        const folderOwner = folder.is_owner === true;
        return (
          <div key={folder.id}>
            <div
              onClick={() => { onToggleExpand(folder.id); if (folder.is_shared) onActiveFolderChange?.(folder.id); }}
              style={{ display:"flex", alignItems:"center", padding:"10px 20px", borderBottom:"1px solid #161616", background: folder.id === activeFolderId ? "#151515" : "#0d0d0d", borderLeft: folder.id === activeFolderId ? "2px solid #5ae09a" : "2px solid transparent", cursor:"pointer", userSelect:"none" }}
            >
              <span style={{ fontSize:10, color:"#888", width:14, flexShrink:0 }}>{isExp ? "▾" : "▸"}</span>
              <span style={{ fontSize:13, marginRight:8 }}>📁</span>
              {editingFolderId === folder.id ? (
                <input
                  ref={folderEditRef}
                  type="text"
                  value={editingFolderName}
                  onClick={e => e.stopPropagation()}
                  onChange={e => onRenameChange(e.target.value)}
                  onKeyDown={e => { if (e.key==="Enter") onCommitRename(); if (e.key==="Escape") onCancelRename(); }}
                  onBlur={onCommitRename}
                  style={{ fontFamily:MONO, fontSize:11, background:"transparent", border:"none", borderBottom:"1px solid #3a3a3a", color:"var(--text)", outline:"none", flex:1, padding:"2px 0" }}
                />
              ) : (
                <span
                  onDoubleClick={folderOwner ? (e => { e.stopPropagation(); onStartRename(folder.id, folder.name); }) : undefined}
                  title={folderOwner ? (lang === "zh" ? "双击重命名" : "Double-click to rename") : undefined}
                  style={{ fontFamily:MONO, fontSize:11, color:"var(--text)", flex:1, cursor: folderOwner ? "text" : "default" }}
                >{folder.name}</span>
              )}
              <span style={{ fontFamily:MONO, fontSize:10, color:"#555", marginRight:8 }}>{inFolder.length}</span>
              {folderOwner ? (
                <button
                  onClick={e => { e.stopPropagation(); onActiveFolderChange?.(folder.id); const next = !folder.is_shared; if (!next) setMembers(prev => ({ ...prev, [folder.id]: [] })); onToggleShared(folder.id, next); }}
                  title={shareTitle(!!folder.is_shared)}
                  style={{ background:"transparent", border:"none", color: folder.is_shared ? "#5ae09a" : "#666", cursor:"pointer", fontSize:13, padding:"0 4px", lineHeight:1, marginRight:2 }}
                >{folder.is_shared ? "🌐" : "🔒"}</button>
              ) : (
                <span
                  onClick={e => { e.stopPropagation(); notifyDenied("denial_share_owner_only"); }}
                  title={lang==="zh" ? "由所有者共享 — 点击了解" : "Shared by the owner — click to learn more"}
                  style={{ fontSize:13, padding:"0 4px", marginRight:2, opacity:0.7, cursor:"help" }}
                >🌐</span>
              )}
              {folderOwner && folder.is_shared && (
                <button onClick={e => { e.stopPropagation(); openInvite(folder.id); }}
                  style={{ background:"transparent", border:"1px solid #3a3a3a", color: inviteFolderId===folder.id ? "var(--text)" : "#666", cursor:"pointer", fontFamily:MONO, fontSize:"0.5rem", letterSpacing:"0.08em", padding:"1px 5px", marginRight:2 }}>
                  INVITE
                </button>
              )}
              {folderOwner && (
                <button onClick={e => { e.stopPropagation(); onRemoveFolder(folder.id); }} style={{ background:"transparent", border:"none", color:"#444", cursor:"pointer", fontSize:13, padding:"0 2px", lineHeight:1 }}>×</button>
              )}
            </div>
            {inviteFolderId === folder.id && (
              <div onClick={e => e.stopPropagation()} style={{ padding:"8px 20px 10px 44px", background:"#0d0d0d", borderBottom:"1px solid #161616" }}>
                <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                  <input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                    onKeyDown={e => e.key==="Enter" && void generateLink(folder.id)}
                    placeholder="colleague@company.com"
                    style={{ fontFamily:MONO, fontSize:11, background:"transparent", border:"none", borderBottom:"1px solid #3a3a3a", color:"var(--text)", outline:"none", flex:1, padding:"2px 0" }} />
                  <button onClick={() => void generateLink(folder.id)} disabled={inviteStatus==="generating"}
                    style={{ fontFamily:MONO, fontSize:"0.5rem", letterSpacing:"0.08em", background:"transparent", border:"1px solid #3a3a3a", color:"var(--text)", cursor:"pointer", padding:"2px 8px", flexShrink:0 }}>
                    {inviteStatus==="generating" ? "…" : (lang==="zh" ? "生成链接" : "GENERATE LINK")}
                  </button>
                </div>
                {inviteStatus==="ready" && inviteLink && (
                  <div style={{ marginTop:6 }}>
                    <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                      <span style={{ flex:1, fontFamily:MONO, fontSize:10, color:"#aaa", wordBreak:"break-all", border:"1px solid #2a2a2a", padding:"4px 6px", background:"#000" }}>{window.location.origin}{inviteLink}</span>
                      <button onClick={copyInviteLink}
                        style={{ fontFamily:MONO, fontSize:"0.5rem", letterSpacing:"0.08em", background:"transparent", border:"1px solid #5ae09a", color:"#5ae09a", cursor:"pointer", padding:"4px 8px", flexShrink:0 }}>
                        {copied ? (lang==="zh" ? "已复制 ✓" : "COPIED ✓") : (lang==="zh" ? "复制" : "COPY")}
                      </button>
                    </div>
                    <div style={{ marginTop:4, fontFamily:MONO, fontSize:9, color:"#666" }}>{lang==="zh" ? "复制链接发送给协作者（暂不支持自动发送）" : "Copy and send this link to your collaborator (auto-delivery coming later)"}</div>
                  </div>
                )}
                {inviteStatus==="full" && <div style={{ marginTop:4, fontFamily:MONO, fontSize:10, color:"#e05555" }}>{lang==="zh" ? "座席已满 — 每个文件夹最多 2 位协作者" : "Seat limit reached — max 2 collaborators per folder"}</div>}
                {inviteStatus==="already" && <div style={{ marginTop:4, fontFamily:MONO, fontSize:10, color:"#e0a055" }}>{lang==="zh" ? "你已经对此人发出过邀请了" : "You've already invited this account"}</div>}
                {inviteStatus==="invalid" && <div style={{ marginTop:4, fontFamily:MONO, fontSize:10, color:"#e05555" }}>{lang==="zh" ? "邮箱格式无效" : "Invalid email address"}</div>}
                {inviteStatus==="error" && <div style={{ marginTop:4, fontFamily:MONO, fontSize:10, color:"#e05555" }}>{lang==="zh" ? "生成失败，请重试" : "Failed — please retry"}</div>}
                {(members[folder.id]?.length ?? 0) > 0 && (
                  <div style={{ marginTop:10, borderTop:"1px solid #1c1c1c", paddingTop:8 }}>
                    <div style={{ fontFamily:MONO, fontSize:9, color:"#666", letterSpacing:"0.08em", marginBottom:4 }}>{membersLabel().toUpperCase()}</div>
                    {members[folder.id].map(m => {
                      const pending = m.status === "pending";
                      const busyId = pending ? m.invite_id : m.user_id;
                      const isBusy = !!busyId && kicking === busyId;
                      return (
                        <div key={m.user_id ?? m.invite_id} title={pending ? pendingTitle() : undefined}
                          style={{ display:"flex", alignItems:"center", gap:6, padding:"2px 0", opacity: pending ? 0.85 : 1 }}>
                          <span style={{ width:8, height:8, borderRadius:"50%", background: pending ? "transparent" : (m.avatar_color ?? "#555"), border: pending ? "1px dashed #6a6a6a" : "none", flexShrink:0 }} />
                          <span style={{ flex:1, fontFamily:MONO, fontSize:10, color: pending ? "#9a9a9a" : "#bbb", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{m.display_name}</span>
                          {pending && <span style={{ fontFamily:MONO, fontSize:8, letterSpacing:"0.08em", color:"#c79a4a", border:"1px solid #5a4a22", borderRadius:2, padding:"0 4px", flexShrink:0 }}>{pendingTag()}</span>}
                          <button onClick={() => { if (pending) { if (m.invite_id) void cancelInvite(folder.id, m.invite_id); } else if (m.user_id) void kickMember(folder.id, m.user_id); }}
                            disabled={isBusy}
                            title={pending ? pendingTitle() : removeTitle()}
                            style={{ background:"transparent", border:"none", color: isBusy ? "#444" : "#e05555", cursor: isBusy ? "default" : "pointer", fontSize:13, lineHeight:1, padding:"0 2px", flexShrink:0 }}>
                            {isBusy ? "…" : "×"}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {isExp && inFolder.map(p => renderPackRow(p, true))}
            {isExp && inFolder.length === 0 && (
              <div style={{ padding:"10px 20px 10px 44px", fontFamily:MONO, fontSize:10, color:"#444", borderBottom:"1px solid #111" }}>{lang === "zh" ? "空文件夹" : "Empty"}</div>
            )}
          </div>
        );
      })}
      {rootPacks.map(p => renderPackRow(p, false))}
    </>
  );
}
